/**
 * Pre-built CQL2 filter presets for aviation data collections.
 *
 * Each preset returns a CQL2-Text string ready to pass to the OGC API
 * via the `filter` parameter.
 */

import {
  eq, neq, gt, gte, lt, lte, between, isNotNull,
  and, or, not, inList, ilike, contains, like,
} from './cql2-filter-builder';
import { dwithin, tAfter, tBefore, tIntersects } from './cql2-spatial';

// ═══════════════════════════════════════════════════════════════════════════════
// AIRPORTS
// ═══════════════════════════════════════════════════════════════════════════════

export interface AirportFilterCriteria {
  /** Airport type: AIRPORT, HELIPORT, SEAPLANE BASE, ULTRALIGHT, GLIDERPORT, BALLOONPORT */
  types?: string[];
  /** Has control tower */
  towered?: boolean;
  /** Has fuel available (any type) */
  fuel?: boolean;
  /** Specific fuel types: 100LL, JET-A, MOGAS, etc. */
  fuelTypes?: string[];
  /** Minimum longest runway length in feet */
  minRunwayLengthFt?: number;
  /** Runway surface types: ASPH, CONC, TURF, GRVL, DIRT, WATER */
  runwaySurfaces?: string[];
  /** Has lighting */
  lighted?: boolean;
  /** Has instrument approach */
  instrumentApproach?: boolean;
  /** ICAO region / country code */
  region?: string;
  /** US state code */
  state?: string;
  /** Status: O (open), CI (closed indefinitely), CP (closed permanently) */
  status?: string[];
  /** Minimum field elevation in feet MSL */
  minElevationFt?: number;
  /** Maximum field elevation in feet MSL */
  maxElevationFt?: number;
  /** Airport use: PU (public), PR (private), MR (military) */
  use?: string[];
  /** Within radius of a point */
  nearPoint?: { lng: number; lat: number; radiusNm: number };
  /** Free-text search across name/ident fields */
  textSearch?: string;
}

export function buildAirportFilter(c: AirportFilterCriteria): string {
  return and(
    c.types?.length ? inList('facility_type', c.types) : undefined,
    c.towered === true ? eq('tower', 'Y') : c.towered === false ? eq('tower', 'N') : undefined,
    c.fuel === true ? isNotNull('fuel_types') : undefined,
    c.fuelTypes?.length ? or(...c.fuelTypes.map((f) => contains('fuel_types', f))) : undefined,
    c.minRunwayLengthFt ? gte('longest_runway_ft', c.minRunwayLengthFt) : undefined,
    c.runwaySurfaces?.length ? or(...c.runwaySurfaces.map((s) => contains('runway_surface', s))) : undefined,
    c.lighted === true ? eq('lighting', 'Y') : undefined,
    c.instrumentApproach === true ? isNotNull('instrument_approach') : undefined,
    c.region ? eq('region', c.region) : undefined,
    c.state ? eq('state_code', c.state) : undefined,
    c.status?.length ? inList('status', c.status) : undefined,
    c.minElevationFt != null ? gte('elevation_ft', c.minElevationFt) : undefined,
    c.maxElevationFt != null ? lte('elevation_ft', c.maxElevationFt) : undefined,
    c.use?.length ? inList('use', c.use) : undefined,
    c.nearPoint ? dwithin('geometry', c.nearPoint.lng, c.nearPoint.lat, c.nearPoint.radiusNm) : undefined,
    c.textSearch ? or(ilike('name', `%${c.textSearch}%`), ilike('ident', `%${c.textSearch}%`), ilike('city', `%${c.textSearch}%`)) : undefined,
  );
}

// ═══════════════════════════════════════════════════════════════════════════════
// AIRSPACE
// ═══════════════════════════════════════════════════════════════════════════════

export interface AirspaceFilterCriteria {
  /** Airspace class: A, B, C, D, E, G */
  classes?: string[];
  /** Airspace type: MOA, R, P, W, A, TFR, CTA, FIR, UIR, ADIZ, etc. */
  types?: string[];
  /** Floor altitude <= value (show airspace that starts at or below this altitude) */
  maxFloorFt?: number;
  /** Ceiling altitude >= value (show airspace that extends above this altitude) */
  minCeilingFt?: number;
  /** Only active / currently effective airspace */
  activeNow?: boolean;
  /** Name / designator search */
  textSearch?: string;
  /** Controlling authority */
  authority?: string;
  /** Within radius */
  nearPoint?: { lng: number; lat: number; radiusNm: number };
}

export function buildAirspaceFilter(c: AirspaceFilterCriteria): string {
  return and(
    c.classes?.length ? inList('class', c.classes) : undefined,
    c.types?.length ? inList('type', c.types) : undefined,
    c.maxFloorFt != null ? lte('floor_ft', c.maxFloorFt) : undefined,
    c.minCeilingFt != null ? gte('ceiling_ft', c.minCeilingFt) : undefined,
    c.activeNow ? tIntersects('schedule', `TIMESTAMP('${new Date().toISOString()}')`) : undefined,
    c.textSearch ? or(ilike('name', `%${c.textSearch}%`), ilike('designator', `%${c.textSearch}%`)) : undefined,
    c.authority ? contains('authority', c.authority) : undefined,
    c.nearPoint ? dwithin('geometry', c.nearPoint.lng, c.nearPoint.lat, c.nearPoint.radiusNm) : undefined,
  );
}

// ═══════════════════════════════════════════════════════════════════════════════
// OBSTACLES
// ═══════════════════════════════════════════════════════════════════════════════

export interface ObstacleFilterCriteria {
  /** Minimum height AGL in feet */
  minAglFt?: number;
  /** Maximum height AGL in feet */
  maxAglFt?: number;
  /** Minimum height MSL in feet */
  minMslFt?: number;
  /** Obstacle type: TOWER, BLDG, STACK, POLE, CRANE, WIND TURBINE, etc. */
  types?: string[];
  /** Lighted (marking type) */
  lighted?: boolean;
  /** Within radius */
  nearPoint?: { lng: number; lat: number; radiusNm: number };
}

export function buildObstacleFilter(c: ObstacleFilterCriteria): string {
  return and(
    c.minAglFt != null ? gte('agl_ft', c.minAglFt) : undefined,
    c.maxAglFt != null ? lte('agl_ft', c.maxAglFt) : undefined,
    c.minMslFt != null ? gte('msl_ft', c.minMslFt) : undefined,
    c.types?.length ? inList('type', c.types) : undefined,
    c.lighted === true ? eq('lighting', 'Y') : c.lighted === false ? neq('lighting', 'Y') : undefined,
    c.nearPoint ? dwithin('geometry', c.nearPoint.lng, c.nearPoint.lat, c.nearPoint.radiusNm) : undefined,
  );
}

// ═══════════════════════════════════════════════════════════════════════════════
// NAVAIDS
// ═══════════════════════════════════════════════════════════════════════════════

export interface NavaidFilterCriteria {
  /** Navaid type: VOR, VORTAC, VOR/DME, TACAN, NDB, NDB/DME, etc. */
  types?: string[];
  /** Navaid class: H (high), L (low), T (terminal) */
  classes?: string[];
  /** Operational status */
  operational?: boolean;
  /** Name or ident search */
  textSearch?: string;
  /** Within radius */
  nearPoint?: { lng: number; lat: number; radiusNm: number };
}

export function buildNavaidFilter(c: NavaidFilterCriteria): string {
  return and(
    c.types?.length ? inList('type', c.types) : undefined,
    c.classes?.length ? inList('class', c.classes) : undefined,
    c.operational === true ? eq('status', 'OPERATIONAL') : undefined,
    c.textSearch ? or(ilike('name', `%${c.textSearch}%`), ilike('ident', `%${c.textSearch}%`)) : undefined,
    c.nearPoint ? dwithin('geometry', c.nearPoint.lng, c.nearPoint.lat, c.nearPoint.radiusNm) : undefined,
  );
}

// ═══════════════════════════════════════════════════════════════════════════════
// TFRs (Temporary Flight Restrictions)
// ═══════════════════════════════════════════════════════════════════════════════

export interface TfrFilterCriteria {
  /** Only currently effective TFRs */
  activeNow?: boolean;
  /** TFR type / reason: HAZARDS, SECURITY, VIP, SPACE_OPERATIONS, SPECIAL, etc. */
  types?: string[];
  /** Minimum altitude affected (ft MSL) */
  minAltFt?: number;
  /** Within radius */
  nearPoint?: { lng: number; lat: number; radiusNm: number };
  /** NOTAM number search */
  notamNumber?: string;
}

export function buildTfrFilter(c: TfrFilterCriteria): string {
  return and(
    c.activeNow ? and(
      tBefore('effective_start', new Date().toISOString()),
      tAfter('effective_end', new Date().toISOString()),
    ) : undefined,
    c.types?.length ? inList('reason', c.types) : undefined,
    c.minAltFt != null ? gte('ceiling_ft', c.minAltFt) : undefined,
    c.nearPoint ? dwithin('geometry', c.nearPoint.lng, c.nearPoint.lat, c.nearPoint.radiusNm) : undefined,
    c.notamNumber ? ilike('notam_number', `%${c.notamNumber}%`) : undefined,
  );
}

// ═══════════════════════════════════════════════════════════════════════════════
// FIXES / REPORTING POINTS
// ═══════════════════════════════════════════════════════════════════════════════

export interface FixFilterCriteria {
  /** Fix type: RNAV, FIX, REP-PT, etc. */
  types?: string[];
  /** Name or ident search */
  textSearch?: string;
  /** On a specific airway */
  airway?: string;
  /** Within radius */
  nearPoint?: { lng: number; lat: number; radiusNm: number };
}

export function buildFixFilter(c: FixFilterCriteria): string {
  return and(
    c.types?.length ? inList('type', c.types) : undefined,
    c.textSearch ? or(ilike('name', `%${c.textSearch}%`), ilike('ident', `%${c.textSearch}%`)) : undefined,
    c.airway ? contains('airways', c.airway) : undefined,
    c.nearPoint ? dwithin('geometry', c.nearPoint.lng, c.nearPoint.lat, c.nearPoint.radiusNm) : undefined,
  );
}

// ═══════════════════════════════════════════════════════════════════════════════
// QUICK PRESETS — common filter combos for aviation use cases
// ═══════════════════════════════════════════════════════════════════════════════

export const QUICK_PRESETS = {
  /** Public airports with paved runways ≥ 3000 ft */
  publicPaved3000: () => buildAirportFilter({
    types: ['AIRPORT'],
    use: ['PU'],
    runwaySurfaces: ['ASPH', 'CONC'],
    minRunwayLengthFt: 3000,
    status: ['O'],
  }),

  /** Towered airports with jet fuel */
  toweredJetFuel: () => buildAirportFilter({
    types: ['AIRPORT'],
    towered: true,
    fuelTypes: ['JET-A'],
    status: ['O'],
  }),

  /** Airports suitable for VFR light aircraft */
  vfrLightAircraft: () => buildAirportFilter({
    types: ['AIRPORT'],
    use: ['PU'],
    minRunwayLengthFt: 2000,
    fuel: true,
    status: ['O'],
  }),

  /** Heliports only */
  heliports: () => buildAirportFilter({ types: ['HELIPORT'], status: ['O'] }),

  /** All SUA that's active now */
  activeSua: () => buildAirspaceFilter({
    types: ['MOA', 'R', 'P', 'W', 'A'],
    activeNow: true,
  }),

  /** Currently effective TFRs */
  activeTfrs: () => buildTfrFilter({ activeNow: true }),

  /** Obstacles ≥ 500 ft AGL */
  tallObstacles: () => buildObstacleFilter({ minAglFt: 500 }),

  /** Obstacles ≥ 1000 ft AGL (towers, wind turbines) */
  veryTallObstacles: () => buildObstacleFilter({ minAglFt: 1000 }),

  /** High-altitude navaids (VOR, VORTAC) */
  highNavaids: () => buildNavaidFilter({
    types: ['VOR', 'VORTAC', 'VOR/DME'],
    classes: ['H'],
    operational: true,
  }),

  /** Terminal navaids */
  terminalNavaids: () => buildNavaidFilter({
    classes: ['T'],
    operational: true,
  }),
} as const;
